import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppstoreOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FilterOutlined,
  HeartFilled,
  HeartOutlined,
  SettingOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserOutlined,
  BarChartOutlined,
  FileTextOutlined,
  SearchOutlined,
  StarFilled,
  ClearOutlined,
  LeftOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { HOTELSRIVER_OFFICIAL_LOGO_URL } from "../../utils/hotelsRiverBrandLogo";
import MockDateRangeField from "../packagesResultsMockup/MockDateRangeField";
import MockOccupancyField, {
  buildOccupancyLabelFromCounts,
} from "../packagesResultsMockup/MockOccupancyField";
import {
  INITIAL_APPLIED_SEARCH,
  MOCK_AGENT_QUICK_FILTERS,
  MOCK_BOARD_OPTIONS,
  MOCK_BOARD_TOP_FILTERS,
  MOCK_FILTER_DESTINATIONS,
  MOCK_HOTELS,
  MOCK_PRICE_FILTERS,
  MOCK_STAR_OPTIONS,
  cloneSearch,
  formatIls,
  formatSearchSummary,
  formatUsd,
  getHotelPerPersonUsd,
  getHotelTotalUsd,
  usdToIls,
  validateSearchDraft,
} from "./mockHotelsData";
import "../packagesResultsMockup/PackagesResultsMockupPage.css";
import "./HotelsResultsMockupPage.css";

const MOCK_CURRENCY_RATES = { USD: 3.089, EUR: 3.4645, GBP: 4.0887 };
const MOCK_HEADER = { messages: 7, leads: 22 };

const NAV_TABS = [
  { id: "search", label: "חיפוש מלונות" },
  { id: "orders", label: "הזמנות", stub: true },
  { id: "customers", label: "לקוחות", stub: true },
];

const RAIL_ITEMS = [
  { id: "search", icon: SearchOutlined, label: "חיפוש" },
  { id: "calendar", icon: CalendarOutlined, label: "יומן", stub: true },
  { id: "team", icon: TeamOutlined, label: "צוות", stub: true },
  { id: "docs", icon: FileTextOutlined, label: "מסמכים", stub: true },
  { id: "analytics", icon: BarChartOutlined, label: "אנליטיקה", stub: true },
  { id: "settings", icon: SettingOutlined, label: "הגדרות", stub: true },
];

function StarRating({ stars }) {
  const count = Math.max(0, Math.min(5, Number(stars) || 0));
  if (!count) return null;
  return (
    <span className="hrm-stars" aria-label={`${count} כוכבים`}>
      {Array.from({ length: count }).map((_, i) => (
        <StarFilled key={i} />
      ))}
    </span>
  );
}

function AllocationBadge({ immediate, variant = "chip" }) {
  const label = immediate ? "אישור מיידי" : "ממתין לאישור";
  const Icon = immediate ? CheckCircleFilled : ClockCircleOutlined;
  return (
    <span
      className={`hrm-alloc hrm-alloc--${immediate ? "immediate" : "pending"} hrm-alloc--${variant}`}
    >
      <Icon aria-hidden />
      {label}
    </span>
  );
}

function BoardTypeTopFilter({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = MOCK_BOARD_TOP_FILTERS.find((o) => o.id === value) || MOCK_BOARD_TOP_FILTERS[0];
  const isPlaceholder = value === "all";

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="hrm-board-dd" ref={rootRef}>
      <button
        type="button"
        className={`hrm-board-dd__trigger${open ? " is-open" : ""}${!isPlaceholder ? " has-value" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{selected.label}</span>
        <SearchOutlined className="hrm-board-dd__ico" aria-hidden />
      </button>
      {open ? (
        <ul className="hrm-board-dd__menu" role="listbox" aria-label="בסיס אירוח">
          {MOCK_BOARD_OPTIONS.map((o) => (
            <li key={o.id} role="option" aria-selected={value === o.id}>
              <button
                type="button"
                className={`hrm-board-dd__option${value === o.id ? " is-active" : ""}`}
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            </li>
          ))}
          {!isPlaceholder ? (
            <li role="option">
              <button
                type="button"
                className="hrm-board-dd__option hrm-board-dd__option--clear"
                onClick={() => {
                  onChange("all");
                  setOpen(false);
                }}
              >
                נקה בחירה
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

function HotelCard({
  hotel,
  viewMode,
  adults,
  compared,
  favorited,
  onToggleCompare,
  onToggleFavorite,
  onSelect,
}) {
  const totalUsd = getHotelTotalUsd(hotel, 1);
  const perPersonUsd = getHotelPerPersonUsd(hotel, adults);
  const totalIls = usdToIls(totalUsd);
  const perPersonIls = usdToIls(perPersonUsd);

  return (
    <article
      className={`hrm-card${viewMode === "grid" ? " hrm-card--grid" : ""}`}
      data-hotel-id={hotel.id}
    >
      <div className="hrm-card__media">
        <img src={hotel.imageUrl} alt={hotel.hotelName} loading="lazy" />
        <div className="hrm-card__media-badges">
          <AllocationBadge immediate={!!hotel.immediateConfirm} variant="overlay" />
        </div>
      </div>

      <div className="hrm-card__body">
        <div className="hrm-card__title-row">
          <h3 className="hrm-card__hotel">{hotel.hotelName}</h3>
          <StarRating stars={hotel.stars} />
        </div>
        <p className="hrm-card__location">
          <EnvironmentOutlined aria-hidden />
          {hotel.area ? `${hotel.area} · ` : ""}
          {hotel.city}, {hotel.country}
        </p>
        <div className="hrm-card__tags">
          <AllocationBadge immediate={!!hotel.immediateConfirm} variant="chip" />
          <span className="hrm-tag">{hotel.boardLabel}</span>
          <span className="hrm-tag">{hotel.roomType}</span>
          {hotel.refundable ? <span className="hrm-tag">ניתן לביטול</span> : null}
          {hotel.beachfront ? <span className="hrm-tag">קרוב לחוף</span> : null}
        </div>
        <div className="hrm-card__meta-row">
          <span>
            <CalendarOutlined aria-hidden />
            {hotel.dateRangeLabel} · {hotel.nights} לילות
          </span>
          <span>
            <HomeOutlined aria-hidden />
            {hotel.rooms} חדר
          </span>
          {hotel.distanceLabel ? (
            <span>
              <EnvironmentOutlined aria-hidden />
              {hotel.distanceLabel}
            </span>
          ) : null}
        </div>
        {hotel.amenities?.length ? (
          <div className="hrm-card__amenities">
            {hotel.amenities.map((a) => (
              <span key={a} className="hrm-amenity">
                {a}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="hrm-card__action">
        <div className="hrm-card__price-block">
          <div className="hrm-card__price">
            <span className="hrm-card__price-fx">{formatUsd(totalUsd)}</span>
            <span className="hrm-card__price-ils">{formatIls(totalIls)}</span>
          </div>
          <div className="hrm-card__price-meta">סה״כ לחדר</div>
          <div className="hrm-card__total">
            לאדם {formatUsd(perPersonUsd)}
            <span className="hrm-card__total-sep" aria-hidden>
              ·
            </span>
            {formatIls(perPersonIls)}
          </div>
        </div>
        <button type="button" className="hrm-btn-primary" onClick={() => onSelect(hotel)}>
          בחר מלון
        </button>
        <div className="hrm-card__tools">
          <label className="hrm-compare">
            <input
              type="checkbox"
              checked={compared}
              onChange={() => onToggleCompare(hotel.id)}
            />
            השוואה
          </label>
          <button
            type="button"
            className={`hrm-fav${favorited ? " hrm-fav--on" : ""}`}
            aria-label={favorited ? "הסר ממועדפים" : "הוסף למועדפים"}
            onClick={() => onToggleFavorite(hotel.id)}
          >
            {favorited ? <HeartFilled /> : <HeartOutlined />}
          </button>
        </div>
      </div>
    </article>
  );
}

export default function HotelsResultsMockupPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("search");
  const [viewMode, setViewMode] = useState("list");
  const [sortBy, setSortBy] = useState("recommended");

  const [appliedSearch, setAppliedSearch] = useState(() => cloneSearch(INITIAL_APPLIED_SEARCH));
  const [draftSearch, setDraftSearch] = useState(() => cloneSearch(INITIAL_APPLIED_SEARCH));
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hotelQuery, setHotelQuery] = useState("");
  const [priceFilter, setPriceFilter] = useState("all");
  const [selectedBoards, setSelectedBoards] = useState(() => new Set());
  const [selectedStars, setSelectedStars] = useState(() => new Set());
  const [agentQuick, setAgentQuick] = useState(() => new Set());
  const [boardTopFilter, setBoardTopFilter] = useState("all");
  const [compareIds, setCompareIds] = useState(() => new Set());
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const [toast, setToast] = useState(null);
  const searchPanelRef = useRef(null);

  const showToast = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2200);
  };

  const toggleInSet = (setter, id) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyBoardTopFilter = (id) => {
    setBoardTopFilter(id);
    if (id === "all") setSelectedBoards(new Set());
    else setSelectedBoards(new Set([id]));
  };

  const openSearchEditor = () => {
    setDraftSearch(cloneSearch(appliedSearch));
    setSearchError(null);
    setSearchOpen(true);
    window.setTimeout(() => {
      searchPanelRef.current?.querySelector("select, input")?.focus?.();
    }, 60);
  };

  const cancelSearchEditor = () => {
    setDraftSearch(cloneSearch(appliedSearch));
    setSearchError(null);
    setSearchOpen(false);
  };

  const patchDraft = (patch) => {
    setDraftSearch((prev) => {
      const next = { ...prev, ...patch };
      if (
        patch.adults != null ||
        patch.children != null ||
        patch.infants != null ||
        patch.rooms != null
      ) {
        next.occupancyLabel =
          patch.occupancyLabel ||
          buildOccupancyLabelFromCounts({
            adults: next.adults,
            children: next.children || 0,
            infants: next.infants || 0,
            rooms: next.rooms,
          });
      }
      if (patch.destinationId) {
        const dest = MOCK_FILTER_DESTINATIONS.find((d) => d.id === patch.destinationId);
        if (dest) next.destinationLabel = dest.label;
      }
      return next;
    });
  };

  const submitSearch = () => {
    const err = validateSearchDraft(draftSearch);
    if (err) {
      setSearchError(err);
      return;
    }
    setSearchLoading(true);
    setSearchError(null);
    window.setTimeout(() => {
      setAppliedSearch(cloneSearch(draftSearch));
      setSearchOpen(false);
      setSearchLoading(false);
      showToast("החיפוש עודכן");
    }, 450);
  };

  const clearFilters = () => {
    setHotelQuery("");
    setPriceFilter("all");
    setSelectedBoards(new Set());
    setSelectedStars(new Set());
    setAgentQuick(new Set());
    setBoardTopFilter("all");
  };

  const filteredHotels = useMemo(() => {
    const q = hotelQuery.trim().toLowerCase();
    const priceRule = MOCK_PRICE_FILTERS.find((p) => p.id === priceFilter) || MOCK_PRICE_FILTERS[0];

    let rows = MOCK_HOTELS.filter((h) => h.destinationId === appliedSearch.destinationId);

    if (q) {
      rows = rows.filter(
        (h) =>
          h.hotelName.toLowerCase().includes(q) ||
          (h.area || "").toLowerCase().includes(q)
      );
    }
    if (selectedBoards.size) {
      rows = rows.filter((h) => selectedBoards.has(h.boardId));
    }
    if (selectedStars.size) {
      rows = rows.filter((h) => {
        if (selectedStars.has(h.stars)) return true;
        if (selectedStars.has(2) && h.stars <= 2) return true;
        return false;
      });
    }
    rows = rows.filter((h) => {
      const p = Number(h.priceTotalUsd) || 0;
      return p >= priceRule.min && p < (priceRule.max === Infinity ? Infinity : priceRule.max + 0.01);
    });
    if (agentQuick.has("immediate")) rows = rows.filter((h) => h.immediateConfirm);
    if (agentQuick.has("refundable")) rows = rows.filter((h) => h.refundable);
    if (agentQuick.has("beach")) rows = rows.filter((h) => h.beachfront);
    if (agentQuick.has("pool")) rows = rows.filter((h) => h.hasPool);

    const sorted = [...rows];
    if (sortBy === "price-asc") sorted.sort((a, b) => a.priceTotalUsd - b.priceTotalUsd);
    else if (sortBy === "price-desc") sorted.sort((a, b) => b.priceTotalUsd - a.priceTotalUsd);
    else if (sortBy === "stars") sorted.sort((a, b) => b.stars - a.stars);
    else sorted.sort((a, b) => Number(b.immediateConfirm) - Number(a.immediateConfirm) || a.priceTotalUsd - b.priceTotalUsd);

    return sorted;
  }, [
    appliedSearch.destinationId,
    hotelQuery,
    priceFilter,
    selectedBoards,
    selectedStars,
    agentQuick,
    sortBy,
  ]);

  const activeFilterCount =
    (hotelQuery.trim() ? 1 : 0) +
    (priceFilter !== "all" ? 1 : 0) +
    selectedBoards.size +
    selectedStars.size +
    agentQuick.size;

  const activeChips = useMemo(() => {
    const chips = [];
    if (hotelQuery.trim()) {
      chips.push({ id: "q", label: `שם: ${hotelQuery.trim()}`, onRemove: () => setHotelQuery("") });
    }
    if (priceFilter !== "all") {
      const p = MOCK_PRICE_FILTERS.find((x) => x.id === priceFilter);
      chips.push({
        id: "price",
        label: p?.label || "מחיר",
        onRemove: () => setPriceFilter("all"),
      });
    }
    selectedBoards.forEach((id) => {
      const o = MOCK_BOARD_OPTIONS.find((b) => b.id === id);
      chips.push({
        id: `board-${id}`,
        label: o?.shortLabel || o?.label || id,
        onRemove: () => toggleInSet(setSelectedBoards, id),
      });
    });
    selectedStars.forEach((id) => {
      const o = MOCK_STAR_OPTIONS.find((s) => s.id === id);
      chips.push({
        id: `star-${id}`,
        label: o?.label || `${id}`,
        onRemove: () => toggleInSet(setSelectedStars, id),
      });
    });
    agentQuick.forEach((id) => {
      const o = MOCK_AGENT_QUICK_FILTERS.find((f) => f.id === id);
      chips.push({
        id: `aq-${id}`,
        label: o?.label || id,
        onRemove: () => toggleInSet(setAgentQuick, id),
      });
    });
    return chips;
  }, [hotelQuery, priceFilter, selectedBoards, selectedStars, agentQuick]);

  const searchSummary = formatSearchSummary(appliedSearch);

  return (
    <div className="hrm-mock hrm-mock--hotels">
      <aside className="hrm-rail" aria-label="ניווט מהיר">
        {RAIL_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = item.id === "search";
          return (
            <button
              key={item.id}
              type="button"
              className={`hrm-rail__btn${active ? " hrm-rail__btn--active" : ""}`}
              title={item.stub ? `${item.label} (בקרוב)` : item.label}
              aria-label={item.label}
              onClick={() => {
                if (item.stub) showToast(`${item.label} — יגיע בהמשך הפלואו`);
              }}
            >
              <Icon />
            </button>
          );
        })}
      </aside>

      <div className="hrm-main-shell">
        <header className="hrm-topbar">
          <div className="hrm-topbar__start">
            <div className="hrm-topbar__brand">
              <img
                src={HOTELSRIVER_OFFICIAL_LOGO_URL}
                alt="HotelsRiver"
                className="hrm-topbar__logo"
              />
              <span className="hrm-mock-pill">מוקאפ מלונות</span>
            </div>
            <nav className="hrm-topbar__tabs" aria-label="ניווט ראשי">
              {NAV_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`hrm-tab${activeTab === tab.id ? " hrm-tab--active" : ""}`}
                  onClick={() => {
                    if (tab.stub) {
                      showToast(`${tab.label} — מסך הבא בפלואו`);
                      return;
                    }
                    setActiveTab(tab.id);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="hrm-topbar__end" aria-label="כלי סוכן">
            <div className="hrm-fx" role="group" aria-label="שערי מטבע">
              <span className="hrm-fx__item hrm-fx__item--usd">
                <abbr title="דולר">$</abbr>
                {MOCK_CURRENCY_RATES.USD.toFixed(4)}
              </span>
              <span className="hrm-fx__item hrm-fx__item--eur">
                <abbr title="יורו">€</abbr>
                {MOCK_CURRENCY_RATES.EUR.toFixed(4)}
              </span>
            </div>
            <div className="hrm-topbar__divider" aria-hidden />
            <button type="button" className="hrm-lang" onClick={() => showToast("בחירת שפה")}>
              <span className="hrm-lang__flag" aria-hidden>
                🇮🇱
              </span>
              <span className="hrm-lang__code">IL</span>
            </button>
            <button
              type="button"
              className="hrm-chip hrm-chip--leads"
              onClick={() => showToast("מעבר ללידים")}
            >
              <span className="hrm-chip__n">{MOCK_HEADER.leads}</span>
              <span className="hrm-chip__t">לידים</span>
            </button>
            <button
              type="button"
              className="hrm-chip hrm-chip--msgs"
              onClick={() => showToast("הודעות תפעול")}
            >
              <span className="hrm-chip__n">{MOCK_HEADER.messages}</span>
              <span className="hrm-chip__t">הודעות</span>
            </button>
            <button
              type="button"
              className="hrm-topbar__user"
              onClick={() => showToast("פרופיל סוכן")}
            >
              <span className="hrm-topbar__user-text">
                <strong>דניאל שמעון</strong>
                <span>סוכן נסיעות</span>
              </span>
              <span className="hrm-avatar" aria-hidden>
                <UserOutlined />
              </span>
            </button>
            <button
              type="button"
              className="hrm-back-btn"
              aria-label="חזרה"
              onClick={() => showToast("חזרה")}
            >
              <LeftOutlined />
            </button>
          </div>
        </header>

        <div className="hrm-sticky">
          <div className="hrm-search-summary" role="search">
            <div className="hrm-search-summary__text">
              <EnvironmentOutlined className="hrm-search-summary__ico" aria-hidden />
              <p className="hrm-search-summary__line">{searchSummary}</p>
            </div>
            <button
              type="button"
              className="hrm-search-summary__edit"
              aria-expanded={searchOpen}
              aria-controls="hrm-hotels-search-editor"
              onClick={() => (searchOpen ? cancelSearchEditor() : openSearchEditor())}
            >
              <EditOutlined aria-hidden />
              שינוי חיפוש
            </button>
          </div>

          <div
            id="hrm-hotels-search-editor"
            ref={searchPanelRef}
            className={`hrm-search-editor${searchOpen ? " is-open" : ""}`}
            aria-hidden={!searchOpen}
          >
            <div className="hrm-search-editor__inner">
              <label className="hrm-search-editor__field">
                <span>יעד</span>
                <select
                  value={draftSearch.destinationId || ""}
                  onChange={(e) => patchDraft({ destinationId: e.target.value })}
                >
                  <option value="">בחרו יעד</option>
                  {MOCK_FILTER_DESTINATIONS.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="hrm-search-editor__field hrm-search-editor__field--picker">
                <span>תאריכים</span>
                <MockDateRangeField
                  checkInStr={draftSearch.dateRangeInput}
                  onChange={(range) => patchDraft(range)}
                  disabled={searchLoading}
                />
              </div>
              <div className="hrm-search-editor__field hrm-search-editor__field--picker hrm-search-editor__field--occ">
                <span>נוסעים וחדרים</span>
                <MockOccupancyField
                  adults={draftSearch.adults}
                  children={draftSearch.children || 0}
                  infants={draftSearch.infants || 0}
                  rooms={draftSearch.rooms}
                  onChange={(occ) => patchDraft(occ)}
                  disabled={searchLoading}
                />
              </div>
              <div className="hrm-search-editor__actions">
                <button
                  type="button"
                  className="hrm-btn-ghost"
                  onClick={cancelSearchEditor}
                  disabled={searchLoading}
                >
                  ביטול
                </button>
                <button
                  type="button"
                  className="hrm-btn-primary"
                  onClick={submitSearch}
                  disabled={searchLoading}
                >
                  {searchLoading ? "מעדכן…" : "עדכון חיפוש"}
                </button>
              </div>
            </div>
            {searchError ? (
              <p className="hrm-search-editor__error" role="alert">
                {searchError}
              </p>
            ) : null}
          </div>

          <div className="hrm-tools" aria-label="מיון וסינון תוצאות">
            <span className="hrm-tools__count">
              {filteredHotels.length} תוצאות
              {appliedSearch.destinationLabel ? ` ב${appliedSearch.destinationLabel}` : ""}
            </span>

            {activeChips.length > 0 ? (
              <div className="hrm-tools__chips">
                {activeChips.map((chip) => (
                  <button key={chip.id} type="button" className="hrm-chip" onClick={chip.onRemove}>
                    {chip.label}
                    <ClearOutlined />
                  </button>
                ))}
                <button type="button" className="hrm-link" onClick={clearFilters}>
                  ניקוי הכול
                </button>
              </div>
            ) : null}

            <div className="hrm-tools__end">
              <BoardTypeTopFilter value={boardTopFilter} onChange={applyBoardTopFilter} />
              <label className="hrm-sort hrm-tools__price">
                <span className="visually-hidden">מחיר</span>
                <select value={priceFilter} onChange={(e) => setPriceFilter(e.target.value)}>
                  {MOCK_PRICE_FILTERS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="hrm-view-toggle" role="group" aria-label="תצוגה">
                <button
                  type="button"
                  className={viewMode === "list" ? "is-active" : ""}
                  aria-pressed={viewMode === "list"}
                  onClick={() => setViewMode("list")}
                  title="תצוגת רשימה"
                >
                  <UnorderedListOutlined />
                </button>
                <button
                  type="button"
                  className={viewMode === "grid" ? "is-active" : ""}
                  aria-pressed={viewMode === "grid"}
                  onClick={() => setViewMode("grid")}
                  title="תצוגת גריד"
                >
                  <AppstoreOutlined />
                </button>
              </div>
              <label className="hrm-sort">
                <span className="visually-hidden">מיון</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="recommended">מומלץ</option>
                  <option value="price-asc">מחיר: מהזול ליקר</option>
                  <option value="price-desc">מחיר: מהיקר לזול</option>
                  <option value="stars">דירוג מלון</option>
                </select>
              </label>
              <button
                type="button"
                className={`hrm-tools__filter-btn${filtersOpen ? " is-open" : ""}`}
                aria-expanded={filtersOpen}
                aria-controls="hrm-hotels-filters-panel"
                onClick={() => setFiltersOpen((v) => !v)}
              >
                <FilterOutlined />
                סינון
                {activeFilterCount > 0 ? (
                  <span className="hrm-tools__filter-count">{activeFilterCount}</span>
                ) : null}
              </button>
            </div>
          </div>

          <div
            id="hrm-hotels-filters-panel"
            className={`hrm-filters-panel${filtersOpen ? " is-open" : ""}`}
            aria-hidden={!filtersOpen}
          >
            <div className="hrm-filters-panel__grid">
              <label className="hrm-filters-panel__field">
                <span>חיפוש מלון</span>
                <input
                  type="search"
                  value={hotelQuery}
                  onChange={(e) => setHotelQuery(e.target.value)}
                  placeholder="שם מלון…"
                />
              </label>

              <fieldset className="hrm-filters-panel__group">
                <legend>בסיס אירוח</legend>
                {MOCK_BOARD_OPTIONS.map((o) => (
                  <label key={o.id}>
                    <input
                      type="checkbox"
                      checked={selectedBoards.has(o.id)}
                      onChange={() => {
                        toggleInSet(setSelectedBoards, o.id);
                        setBoardTopFilter("all");
                      }}
                    />
                    {o.shortLabel || o.label}
                  </label>
                ))}
              </fieldset>

              <fieldset className="hrm-filters-panel__group">
                <legend>דירוג</legend>
                {MOCK_STAR_OPTIONS.map((o) => (
                  <label key={o.id}>
                    <input
                      type="checkbox"
                      checked={selectedStars.has(o.id)}
                      onChange={() => toggleInSet(setSelectedStars, o.id)}
                    />
                    {o.label}
                  </label>
                ))}
              </fieldset>

              <fieldset className="hrm-filters-panel__group">
                <legend>לסוכן</legend>
                {MOCK_AGENT_QUICK_FILTERS.map((f) => (
                  <label key={f.id}>
                    <input
                      type="checkbox"
                      checked={agentQuick.has(f.id)}
                      onChange={() => toggleInSet(setAgentQuick, f.id)}
                    />
                    {f.label}
                  </label>
                ))}
              </fieldset>
            </div>
            {activeFilterCount > 0 ? (
              <div className="hrm-filters-panel__foot">
                <button type="button" className="hrm-link" onClick={clearFilters}>
                  ניקוי הכול
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className={`hrm-workspace hrm-workspace--full${searchLoading ? " is-loading" : ""}`}>
          {searchLoading ? (
            <div className="hrm-loading" role="status">
              מעדכן תוצאות…
            </div>
          ) : null}
          <section className="hrm-results" aria-label="תוצאות מלונות">
            {filteredHotels.length === 0 ? (
              <div className="hrm-empty">
                <p>אין מלונות שתואמים את הסינון.</p>
                <button type="button" className="hrm-link" onClick={clearFilters}>
                  נקה סינונים
                </button>
                <button type="button" className="hrm-btn-ghost" onClick={openSearchEditor}>
                  שינוי חיפוש
                </button>
              </div>
            ) : (
              <div className={viewMode === "grid" ? "hrm-results__grid" : "hrm-results__list"}>
                {filteredHotels.map((hotel) => (
                  <HotelCard
                    key={hotel.id}
                    hotel={hotel}
                    viewMode={viewMode}
                    adults={appliedSearch.adults}
                    compared={compareIds.has(hotel.id)}
                    favorited={favoriteIds.has(hotel.id)}
                    onToggleCompare={(id) => toggleInSet(setCompareIds, id)}
                    onToggleFavorite={(id) => toggleInSet(setFavoriteIds, id)}
                    onSelect={(h) =>
                      navigate(`/dev/hotels-profile-mockup?hotelId=${encodeURIComponent(h.id)}`)
                    }
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {toast ? (
        <div className="hrm-toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
